// src/components/Dashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { useAuth } from '../context/AuthContext';
import Invitations from './Invitations';
import { fetchEditingRequests, fetchMyInvitations, fetchVideos } from '../graphqlService';
import { brand, statusTone } from '../theme';
import { getStatusGroup, StatusGroup } from '../utils/projectStatus';
import { formatBytes, formatRelativeTime, pluralize } from '../utils/format';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import StatCard from './ui/StatCard';
import StatusChip from './ui/StatusChip';
import EmptyState from './ui/EmptyState';
import { objectIdTime, sortLibraryItems } from './Library/libraryFormat';

interface ProjectObject {
  id: string;
  projectName: string;
  status: string;
  videoId?: string;
  videoName?: string;
  editorName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface VideoObject {
  id: string;
  name?: string;
  size?: number | null;
  duration?: string | null;
}

const RECENT_LIMIT = 5;

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const lastActivity = (p: ProjectObject) => p.updatedAt || p.createdAt || '';

/** One divided row inside a Section. */
const Row: React.FC<{ to: string; children: React.ReactNode; action?: React.ReactNode }> = ({ to, children, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: 2.5,
      py: 1.5,
      borderTop: 1,
      borderColor: 'divider',
      '&:first-of-type': { borderTop: 0 },
    }}
  >
    <Box
      component={Link}
      to={to}
      sx={{
        flex: 1,
        minWidth: 0,
        color: 'inherit',
        textDecoration: 'none',
        '&:hover .row-title': { color: brand.copperDark, textDecoration: 'underline' },
        '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2, borderRadius: 1 },
      }}
    >
      {children}
    </Box>
    {action}
  </Box>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectObject[]>([]);
  const [videos, setVideos] = useState<VideoObject[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(true);
  const [hasInvitations, setHasInvitations] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadProjects = async () => {
      try {
        setLoading(true);
        const token = await user?.getIdToken(true);
        if (token) {
          const fetchedProjects = await fetchEditingRequests(1, 50, token);
          if (cancelled) return;
          setProjects(fetchedProjects?.requests || []);
          setLoadError(null);
        }
      } catch (error) {
        // An outage must not be presented as "you have no projects".
        console.error('Failed to load projects:', error);
        if (cancelled) return;
        setProjects([]);
        setLoadError("We couldn't load your projects. Check your connection and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadVideos = async () => {
      try {
        setVideosLoading(true);
        const fetched = await fetchVideos();
        if (!cancelled) setVideos(Array.isArray(fetched) ? fetched.filter(Boolean) : []);
      } catch (error) {
        console.error('Failed to load videos:', error);
        if (!cancelled) setVideos([]);
      } finally {
        if (!cancelled) setVideosLoading(false);
      }
    };

    const checkInvitations = async () => {
      try {
        const invitations = await fetchMyInvitations();
        if (!cancelled) setHasInvitations(Boolean(invitations && invitations.length > 0));
      } catch (error) {
        console.error('Error checking invitations:', error);
        if (!cancelled) setHasInvitations(false);
      }
    };

    loadProjects();
    loadVideos();
    checkInvitations();
    return () => {
      cancelled = true;
    };
  }, [user, reloadKey]);

  // Every status maps to one group (utils/projectStatus), so these add up.
  // The old buckets skipped Client review, Draft ready, Revision requested and
  // Cancelled, so a cut waiting for the creator's review was counted nowhere.
  const countInGroup = (group: StatusGroup) => projects.filter((p) => getStatusGroup(p?.status) === group).length;
  const reviewCount = countInGroup('needs-review');
  const inProgressCount = countInGroup('in-progress');
  const waitingCount = countInGroup('waiting');
  const deliveredCount = countInGroup('done');

  const needsReview = useMemo(
    () =>
      projects
        .filter((p) => getStatusGroup(p?.status) === 'needs-review')
        .sort((a, b) => lastActivity(b).localeCompare(lastActivity(a))),
    [projects]
  );

  const recentProjects = useMemo(
    () => [...projects].sort((a, b) => lastActivity(b).localeCompare(lastActivity(a))).slice(0, RECENT_LIMIT),
    [projects]
  );

  // Videos carry no upload date; their ids are ObjectIds minted at upload, so
  // the id's timestamp orders them (the API returns oldest first).
  const recentVideos = useMemo(
    () => sortLibraryItems(videos, 'newest', { time: (v) => objectIdTime(v.id), name: (v) => v.name || '' }).slice(0, RECENT_LIMIT),
    [videos]
  );

  const firstName = user?.displayName ? user.displayName.trim().split(/\s+/)[0] : '';
  const isNewAccount = !loading && !videosLoading && !loadError && projects.length === 0 && videos.length === 0;
  // Only when there is something to answer; an editor with no pending
  // invitations used to get an empty "No pending invitations" card here.
  const showInvitations = hasInvitations;

  return (
    <PageContainer>
      <PageHeader
        title={`${getGreeting()}${firstName ? `, ${firstName}` : ''}`}
        subtitle="What needs you, and what's moving."
        action={
          <Button component={Link} to="/submit-editing-request" variant="contained" startIcon={<AddOutlinedIcon />}>
            New project
          </Button>
        }
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {isNewAccount ? (
        <Section title="Get started" description="Three steps from raw footage to a brief your editor can work from.">
          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2.5 }}>
            {[
              { n: 1, title: 'Upload a video', body: 'Add the footage you want edited to your library.', to: '/upload', cta: 'Upload a video' },
              { n: 2, title: 'Annotate it', body: 'Leave notes at exact moments so your editor knows what you want.', to: '/library', cta: null },
              { n: 3, title: 'Create a project', body: 'Add a brief, invite your editor and send it.', to: '/submit-editing-request', cta: null },
            ].map((step) => (
              <Box component="li" key={step.n} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    color: brand.slate,
                    border: `1px solid ${brand.borderStrong}`,
                    backgroundColor: 'grey.100',
                  }}
                >
                  {step.n}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.body}
                  </Typography>
                </Box>
                {step.cta && (
                  <Button component={Link} to={step.to} variant="outlined" size="small">
                    {step.cta}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </Section>
      ) : (
        <>
          {/* Neutral tiles; only "Needs your review" turns amber, and only
              when something is actually waiting for the creator. */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<RateReviewOutlinedIcon />}
                label="Needs your review"
                value={loading ? '—' : reviewCount}
                color={!loading && reviewCount > 0 ? statusTone.attention.main : brand.slate}
                to="/my-projects?status=needs-review"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<TimelapseOutlinedIcon />}
                label="In progress"
                value={loading ? '—' : inProgressCount}
                to="/my-projects?status=in-progress"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<HourglassEmptyOutlinedIcon />}
                label="Waiting for an editor"
                value={loading ? '—' : waitingCount}
                to="/my-projects?status=waiting"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<TaskAltOutlinedIcon />}
                label="Delivered"
                value={loading ? '—' : deliveredCount}
                to="/my-projects?status=done"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Section
                title="Needs your attention"
                description="Cuts from your editors, waiting for your review."
                disablePadding
              >
                {loading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                    Loading…
                  </Typography>
                ) : needsReview.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2.5 }}>
                    Nothing is waiting for you right now.
                  </Typography>
                ) : (
                  needsReview.map((p) => (
                    <Row
                      key={p.id}
                      to={`/project-details/${p.id}`}
                      action={
                        <Button component={Link} to={`/project-details/${p.id}`} size="small" variant="outlined">
                          Review
                        </Button>
                      }
                    >
                      <Typography className="row-title" sx={{ fontWeight: 600 }} noWrap>
                        {p.projectName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {p.editorName ? `From ${p.editorName}` : 'From your editor'}
                        {lastActivity(p) ? ` · ${formatRelativeTime(lastActivity(p))}` : ''}
                      </Typography>
                    </Row>
                  ))
                )}
              </Section>
            </Grid>
            <Grid item xs={12} md={5}>
              <Section
                title="Recent uploads"
                action={
                  <Button component={Link} to="/library" size="small">
                    Open library
                  </Button>
                }
                disablePadding
              >
                {videosLoading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                    Loading…
                  </Typography>
                ) : recentVideos.length === 0 ? (
                  <EmptyState
                    compact
                    icon={<VideoLibraryOutlinedIcon />}
                    title="No videos yet"
                    description="Upload footage to your library to annotate it."
                    actionLabel="Upload a video"
                    onAction={() => navigate('/upload')}
                  />
                ) : (
                  recentVideos.map((v) => (
                    <Row key={v.id} to={`/annotate/${v.id}`}>
                      <Typography className="row-title" sx={{ fontWeight: 600 }} noWrap>
                        {v.name || 'Untitled video'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {[v.duration, typeof v.size === 'number' ? formatBytes(v.size) : null].filter(Boolean).join(' · ')}
                      </Typography>
                    </Row>
                  ))
                )}
              </Section>
            </Grid>
          </Grid>

          <Section
            title="Recent projects"
            description={loading ? undefined : `${pluralize(projects.length, 'project')} in total.`}
            action={
              <Button component={Link} to="/my-projects" size="small">
                All projects
              </Button>
            }
            disablePadding
            sx={{ mb: 3 }}
          >
            {loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                Loading…
              </Typography>
            ) : recentProjects.length === 0 ? (
              <EmptyState
                compact
                icon={<FolderOpenOutlinedIcon />}
                title="No projects yet"
                description="A project sends an annotated video and a brief to your editor."
                actionLabel="New project"
                onAction={() => navigate('/submit-editing-request')}
              />
            ) : (
              recentProjects.map((p) => (
                <Row key={p.id} to={`/project-details/${p.id}`} action={<StatusChip kind="project" status={p.status} />}>
                  <Typography className="row-title" sx={{ fontWeight: 600 }} noWrap>
                    {p.projectName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {p.editorName ? p.editorName : 'No editor yet'}
                    {lastActivity(p) ? ` · Updated ${formatRelativeTime(lastActivity(p))}` : ''}
                  </Typography>
                </Row>
              ))
            )}
          </Section>
        </>
      )}

      {showInvitations && (
        <Box sx={{ mt: 3 }}>
          {/* Invitations frames itself (title + divider rows), as on the editor's Projects page. */}
          <Invitations hideWhenEmpty title="Editor invitations" description="Invitations to edit for other creators." />
        </Box>
      )}
    </PageContainer>
  );
};

export default Dashboard;
